import { MoreHorizontal } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@shigo-ui/ui';

export interface DashboardDetailsMetadataEntry {
  /** Unique within the header; also the entry's key. */
  label: string;
  value: ReactNode;
}

export interface DashboardDetailsHeaderProps {
  /** The Back control. */
  back: ReactNode;
  /** The page's h1. */
  title: ReactNode;
  /** Facts about the object, one quiet dot-separated line under the title; empty values are hidden. */
  metadata?: readonly DashboardDetailsMetadataEntry[] | undefined;
  /** The main job, usually Edit, as a small ghost button right after the title. */
  primaryAction?: ReactNode | undefined;
  /** Frequent secondary tools, shown as quiet ghost buttons. Keep to one or two. */
  actions?: ReactNode | undefined;
  /** Further tools as `DropdownMenuItem`s, one level deeper behind a More button. */
  moreActions?: ReactNode | undefined;
  moreActionsLabel?: string | undefined;
}

/**
 * Header of a details page: Back, the object's name with its quiet actions right after it, its
 * facts underneath, and the rest of its tools one level deeper. No icon tiles, no bordered strips.
 */
export function DashboardDetailsHeader({
  back,
  title,
  metadata,
  primaryAction,
  actions,
  moreActions,
  moreActionsLabel = 'More actions',
}: DashboardDetailsHeaderProps) {
  const hasActions = Boolean(primaryAction ?? actions ?? moreActions);
  const facts = (metadata ?? []).filter((entry) => !isMissingValue(entry.value));
  return (
    <header
      data-slot="dashboard-details-header"
      className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-1"
    >
      <div className="flex h-9 items-center">{back}</div>
      <div className="flex min-h-9 min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
        <div className="min-w-0">{title}</div>
        {hasActions ? (
          // One small bordered group, so the actions read as buttons and not as part of the title.
          <div
            data-slot="dashboard-details-header-actions"
            className="bg-card flex h-8 flex-none items-stretch divide-x divide-[var(--border-strong)] overflow-hidden rounded-md border border-[var(--border-strong)] [&>*]:h-full [&>*]:rounded-none"
          >
            {actions}
            {primaryAction}
            {moreActions ? (
              // Not modal: items open dialogs, which must not fight the menu's focus trap.
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="w-8"
                    aria-label={moreActionsLabel}
                  >
                    <MoreHorizontal className="size-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-52">
                  {moreActions}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        ) : null}
      </div>
      {facts.length > 0 ? (
        <dl
          data-slot="dashboard-details-metadata"
          className="col-start-2 flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-1 text-[0.78125rem] leading-5 text-[var(--text-tertiary)]"
        >
          {facts.map((entry, index) => (
            <div key={entry.label} className="flex max-w-full min-w-0 items-baseline gap-1.5">
              {index > 0 ? (
                <span aria-hidden="true" className="text-[var(--border-strong)]">
                  •
                </span>
              ) : null}
              <dt className="flex-none">{entry.label}</dt>
              <dd className="text-muted-foreground min-w-0 truncate tabular-nums">{entry.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </header>
  );
}

function isMissingValue(value: ReactNode) {
  return value === null || value === undefined || value === '' || value === '-' || value === '—';
}
